import { FacetGroup } from 'models/FacetGroup';
import { invokeIfExists } from 'utils';
import { RangeFacetFilter } from 'models/RangeFacetFilter';
import { ValueFacetFilter } from 'models/ValueFacetFilter';
import { FilterDisplayType } from 'models/FilterDisplayType';
import {
  GroupChoice,
  mapSelectedFilterValuesToString,
  unitFormatMap,
  type ValueChoice,
  type ChoiceFilter
} from 'components/Filter/utils';
import { getUnit } from 'components/Filter/themes/BGH-RD3/types/utils/unit';
import { FacetFilter } from 'models/FacetFilter';

type GetActiveChoicesProps = {
  filterPanels: FacetFilter[];
  currencySymbol: string;
};

/**
 * Returns list of the active Filters as a list of ChoiceFilters
 *
 * @param filterPanels The filter panels to search for active filters in
 * @param currencySymbol The currency symbol to be used for range filters that need them (Price filter)
 */
export const getActiveChoices = ({
  filterPanels,
  currencySymbol
}: GetActiveChoicesProps): ChoiceFilter[] => {
  const activePanels = filterPanels?.filter(filterPanel => filterPanel.active);

  return activePanels
    ?.filter(panel => filterToChoiceMap[panel.type])
    .flatMap(panel => mapFilterToChoice(panel, currencySymbol));
};

type FilterToChoiceMap = {
  [key: string]: (facetFilter: FacetFilter, currencySymbol: string) => ChoiceFilter;
};

/**
 * Map containing the different mapper functions depending on the type of Filter Display
 */
const filterToChoiceMap: FilterToChoiceMap = {
  [FilterDisplayType.Group]: mapGroupFilterToChoice,
  [FilterDisplayType.SimpleSlider]: mapRangeFilterToChoice,
  [FilterDisplayType.Standard]: mapValueFilterToChoice,
  [FilterDisplayType.NoHeader]: mapValueFilterToChoice,
  [FilterDisplayType.Category]: mapValueFilterToChoice
};

const mapFilterToChoice = (facetFilter: FacetFilter, currencySymbol: string) => {
  return invokeIfExists(
    filterToChoiceMap,
    facetFilter.type,
    facetFilter,
    facetFilter,
    currencySymbol
  );
};

function mapValueFilterToChoice({
  name,
  displayName,
  values,
  type
}: ValueFacetFilter): ValueChoice {
  return {
    prefix: type === FilterDisplayType.NoHeader ? undefined : displayName,
    displayName: mapSelectedFilterValuesToString(values),
    name
  };
}

function mapRangeFilterToChoice(facet: RangeFacetFilter, currencySymbol: string): ValueChoice {
  const { name, displayName, selectedMin, selectedMax } = facet;

  return {
    prefix: displayName,
    displayName: `${unitFormatMap(name)(selectedMin)}-${unitFormatMap(name)(selectedMax)} ${getUnit(
      {
        name,
        displayName,
        currencySymbol
      }
    )}`.trimEnd(),
    name
  };
}

function mapGroupFilterToChoice(facetGroup: FacetGroup, currencySymbol: string): GroupChoice {
  const { facets, name, displayName } = facetGroup;

  const mappedChoices = getActiveChoices({ filterPanels: facets, currencySymbol });

  return {
    name,
    prefix: displayName,
    facets: mappedChoices
  };
}
